import { useTranslation } from '@pancakeswap/localization'
import { Box, Button, EmptyState, ErrorState, Flex, LoadingState, NextLinkFromReactRouter, Tag, Text } from '@pancakeswap/uikit'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import type { DataMeta, PoolStat, TokenStat } from 'server/info/types'
import Percent from 'views/Info/components/Percent'
import { DexId, VersionParam } from 'state/infoHub/hooks'
import { CurrencyLogo, DoubleCurrencyLogo } from 'views/Info/components/CurrencyLogo'
import { feeTierLabel, num, shortAddress, usd } from './format'

/**
 * Tables that scroll inside their own container (responsive requirement): the page never scrolls
 * horizontally; on narrow screens the table keeps every column and the wrapper scrolls.
 */
const Scroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
`
const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  font-size: 14px;
  th,
  td {
    padding: 10px 12px;
    text-align: right;
    white-space: nowrap;
    border-top: 1px solid ${({ theme }) => theme.colors.cardBorder};
  }
  th {
    border-top: 0;
    color: ${({ theme }) => theme.colors.textSubtle};
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    cursor: pointer;
    user-select: none;
  }
  tbody tr:hover td {
    background: ${({ theme }) => theme.colors.background};
  }
  a {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
  }
  td.rank,
  th.rank {
    width: 36px;
    color: ${({ theme }) => theme.colors.textSubtle};
    text-align: right;
    padding-right: 4px;
    position: sticky;
    left: 0;
    background: ${({ theme }) => theme.colors.backgroundAlt};
    z-index: 1;
    cursor: default;
  }
  th:nth-child(2),
  td:nth-child(2) {
    text-align: left;
    position: sticky;
    left: 36px;
    background: ${({ theme }) => theme.colors.backgroundAlt};
    z-index: 1;
  }
`
const Name = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  vertical-align: middle;
`
/* site review 59 #8 (2026-10-08): nothing under 12 px; the scale's small step is 12 (13 body, 14 cells) */
const Sub = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSubtle};
  font-weight: 400;
`
const Fee = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.input};
  color: ${({ theme }) => theme.colors.textSubtle};
`
/** A pair or token link in a row: the text is 15 px tall, the hit area 44 (padding, pulled back with margins so the row keeps its height). */
const RowLink = styled(NextLinkFromReactRouter)`
  display: inline-block;
  padding: 15px 0;
  margin: -15px 0;
  line-height: 14px;
`
const Cell: React.FC<{ value: number | null | undefined; kind?: 'usd' | 'num'; title?: string }> = ({ value, kind = 'usd', title }) =>
  value === null || value === undefined ? (
    <Text as="span" color="failure" title={title ?? 'Unavailable (no price path or source down)'}>
      —
    </Text>
  ) : (
    <span>{kind === 'usd' ? usd(value) : num(value, { isInteger: true })}</span>
  )

const PAGE = 10
const Pager: React.FC<{ page: number; pages: number; onPage: (p: number) => void }> = ({ page, pages, onPage }) => {
  const { t } = useTranslation()
  if (pages <= 1) return null
  return (
    <Flex justifyContent="center" alignItems="center" py="12px" style={{ gap: 12 }}>
      <Button scale="sm" variant="text" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t('Previous page')} style={{ minWidth: 44, minHeight: 44 }}>
        ←
      </Button>
      <Text fontSize="14px">
        {t('Page %page% of %maxPage%', { page, maxPage: pages })}
      </Text>
      <Button scale="sm" variant="text" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t('Next page')} style={{ minWidth: 44, minHeight: 44 }}>
        →
      </Button>
    </Flex>
  )
}

const SectionState: React.FC<{ meta?: DataMeta; loading?: boolean; error?: Error; rows?: number; onRetry?: () => void; emptyTitle: string; emptyDescription?: string }> = ({ meta, loading, error, rows, onRetry, emptyTitle, emptyDescription }) => {
  const { t } = useTranslation()
  if (loading && rows === undefined) return <LoadingState rows={5} label={t('Loading')} />
  if (error && rows === undefined) return <ErrorState title={t('Could not load')} description={error.message} onRetry={onRetry} retryLabel={t('Retry')} py="24px" />
  if (meta?.state === 'unavailable') return <ErrorState title={t('Source unavailable')} description={meta.error} onRetry={onRetry} retryLabel={t('Retry')} py="24px" />
  if (rows === 0) return <EmptyState title={emptyTitle} description={emptyDescription} py="24px" />
  return null
}

type PoolSortKey = 'tvlUSD' | 'volume24hUSD' | 'volume7dUSD' | 'txCount24h'
export const PoolStatTable: React.FC<{ pools?: PoolStat[] | null; meta?: DataMeta; loading?: boolean; error?: Error; onRetry?: () => void; dex: DexId; version: VersionParam; limit?: number }> = ({ pools, meta, loading, error, onRetry, dex, version, limit }) => {
  const { t } = useTranslation()
  const [sort, setSort] = useState<{ key: PoolSortKey; dir: 1 | -1 }>({ key: 'tvlUSD', dir: -1 })
  const [page, setPage] = useState(1)
  const rows = useMemo(() => {
    const list = (pools ?? []).slice().sort((a, b) => {
      const av = a[sort.key] ?? -1
      const bv = b[sort.key] ?? -1
      return (av - bv) * sort.dir
    })
    return limit ? list.slice(0, limit) : list
  }, [pools, sort, limit])
  const pages = Math.max(1, Math.ceil(rows.length / PAGE))
  const current = Math.min(page, pages)
  const visible = rows.slice((current - 1) * PAGE, current * PAGE)
  const head = (label: string, key: PoolSortKey) => (
    <th onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((s.dir * -1) as 1 | -1) : -1 }))} aria-sort={sort.key === key ? (sort.dir === -1 ? 'descending' : 'ascending') : 'none'}>
      {label}
      {sort.key === key ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''}
    </th>
  )
  const state = <SectionState meta={meta} loading={loading} error={error} rows={pools ? rows.length : undefined} onRetry={onRetry} emptyTitle={t('No pools')} emptyDescription={meta?.coverage} />
  if (state && (!pools || rows.length === 0)) return state
  return (
    <Box>
      <Scroll>
        <Table data-testid="pool-table">
          <thead>
            <tr>
              <th className="rank">#</th>
              <th>{t('Pool')}</th>
              {head(t('TVL'), 'tvlUSD')}
              {head(t('Volume 24H'), 'volume24hUSD')}
              {head(t('Volume 7D'), 'volume7dUSD')}
              <th>{t('LP fees 24H')}</th>
              {head(t('Swaps 24H'), 'txCount24h')}
              <th>{t('Active liquidity')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p, i) => (
              <tr key={`${p.dex}-${p.address}`}>
                <td className="rank">{(current - 1) * PAGE + i + 1}</td>
                <td>
                  <Name>
                    <DoubleCurrencyLogo address0={p.token0.address} address1={p.token1.address} size={18} />
                    <RowLink to={`/info/${dex}/pools/${p.address}?v=${p.version}`}>
                      {p.token0.symbol}/{p.token1.symbol}
                    </RowLink>
                    <Fee title={`${p.version.toUpperCase()} pool, LP fee ${(p.lpFee * 100).toFixed(2)}%`}>
                      {p.version.toUpperCase()}
                      {p.feeTier !== undefined ? ` · ${feeTierLabel(p.feeTier)}` : ''}
                    </Fee>
                    {version === 'all' && p.dex !== dex && <Tag scale="sm">{p.dex}</Tag>}
                    <Sub title={p.address}>{shortAddress(p.address)}</Sub>
                  </Name>
                </td>
                <td title={p.tvlMethod}>
                  <Cell value={p.tvlUSD} />
                </td>
                <td title={p.volumeMethod}>
                  <Cell value={p.volume24hUSD} /> {p.volume24hChange !== null && p.volume24hChange !== 0 && <Percent value={p.volume24hChange} fontSize="12px" display="inline" />}
                </td>
                <td title={p.volumeMethod}>
                  <Cell value={p.volume7dUSD} /> {p.volume7dChange !== null && p.volume7dChange !== 0 && <Percent value={p.volume7dChange} fontSize="12px" display="inline" />}
                </td>
                <td title={`${(p.lpFee * 100).toFixed(2)}% of the 24 h volume`}>
                  <Cell value={p.volume24hUSD === null ? null : p.volume24hUSD * p.lpFee} />
                </td>
                <td>
                  <Cell value={p.txCount24h} kind="num" />
                </td>
                <td>{p.version === 'v3' ? (p.activeLiquidity === '0' ? <Text as="span" color="warning">{t('none in range')}</Text> : p.activeLiquidity ? t('in range') : '—') : t('n/a (V2)')}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
      {!limit && <Pager page={current} pages={pages} onPage={setPage} />}
    </Box>
  )
}

type TokenSortKey = 'priceUSD' | 'volume24hUSD' | 'liquidityUSD' | 'txCount24h'
export const TokenStatTable: React.FC<{ tokens?: TokenStat[] | null; meta?: DataMeta; loading?: boolean; error?: Error; onRetry?: () => void; dex: DexId; version: VersionParam; limit?: number }> = ({ tokens, meta, loading, error, onRetry, dex, version, limit }) => {
  const { t } = useTranslation()
  const [sort, setSort] = useState<{ key: TokenSortKey; dir: 1 | -1 }>({ key: 'liquidityUSD', dir: -1 })
  const [page, setPage] = useState(1)
  const rows = useMemo(() => {
    const list = (tokens ?? []).slice().sort((a, b) => ((a[sort.key] ?? -1) - (b[sort.key] ?? -1)) * sort.dir)
    return limit ? list.slice(0, limit) : list
  }, [tokens, sort, limit])
  const pages = Math.max(1, Math.ceil(rows.length / PAGE))
  const current = Math.min(page, pages)
  const visible = rows.slice((current - 1) * PAGE, current * PAGE)
  const head = (label: string, key: TokenSortKey) => (
    <th onClick={() => setSort((s) => ({ key, dir: s.key === key ? ((s.dir * -1) as 1 | -1) : -1 }))}>
      {label}
      {sort.key === key ? (sort.dir === -1 ? ' ↓' : ' ↑') : ''}
    </th>
  )
  const state = <SectionState meta={meta} loading={loading} error={error} rows={tokens ? rows.length : undefined} onRetry={onRetry} emptyTitle={t('No tokens')} emptyDescription={meta?.coverage} />
  if (state && (!tokens || rows.length === 0)) return state
  const vq = version === 'all' ? '' : `?v=${version}`
  return (
    <Box>
      <Scroll>
        <Table data-testid="token-table">
          <thead>
            <tr>
              <th className="rank">#</th>
              <th>{t('Token')}</th>
              {head(t('Price'), 'priceUSD')}
              <th>{t('24H')}</th>
              {head(t('Volume 24H'), 'volume24hUSD')}
              {head(t('Liquidity'), 'liquidityUSD')}
              <th>{t('Pools')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((tk, i) => (
              <tr key={`${tk.chainId}:${tk.address}`}>
                <td className="rank">{(current - 1) * PAGE + i + 1}</td>
                <td>
                  <Name>
                    <CurrencyLogo address={tk.address} size="24px" />
                    <RowLink to={`/info/${dex}/tokens/${tk.address}${vq}`}>{tk.symbol}</RowLink>
                    <Sub>{tk.name}</Sub>
                    <Sub title={`chain ${tk.chainId} · ${tk.address}`}>{shortAddress(tk.address)}</Sub>
                  </Name>
                </td>
                <td title={tk.priceSource ?? 'no price path'}>{tk.priceUSD === null ? <Cell value={null} title="No price path (no pool with a priced counter-asset)" /> : `$${tk.priceUSD < 0.001 ? tk.priceUSD.toExponential(3) : num(tk.priceUSD)}`}</td>
                <td>{tk.priceChange24h === null ? <Text as="span" color="textSubtle">—</Text> : tk.priceChange24h === 0 ? <Text as="span" color="textSubtle">0.00%</Text> : <Percent value={tk.priceChange24h} display="inline" />}</td>
                <td>
                  <Cell value={tk.volume24hUSD} />
                </td>
                <td>
                  <Cell value={tk.liquidityUSD} />
                </td>
                <td>{tk.poolCount}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroll>
      {!limit && <Pager page={current} pages={pages} onPage={setPage} />}
    </Box>
  )
}
